import { useId } from "react";

/**
 * The redesign's card (guide §3): surface background, 1 px `--color-border-subtle`, radius 12. The default is
 * padded (20 px); `flush` removes the padding and clips overflow for a card that holds a table. With a `title`,
 * the card renders the 16/600 header with the optional 13 px muted caption and names itself after it.
 */
export function Card({
  title,
  caption,
  aside,
  flush = false,
  className,
  children,
}: {
  title?: string;
  /** Muted 13 px line. Below the title by default (Main/Scope); pass `aside` instead for Settings' row header. */
  caption?: string;
  /** Right side of the header (a pill, or the caption inline) — switches the header to the row layout. */
  aside?: React.ReactNode;
  /** No padding and `overflow: hidden` — for cards whose body is a table (§3). */
  flush?: boolean;
  className?: string;
  children: React.ReactNode;
}): JSX.Element {
  const titleId = useId();
  const classes = ["card", flush ? "card--flush" : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <section className={classes} aria-labelledby={title === undefined ? undefined : titleId}>
      {title === undefined ? null : (
        <div className={aside === undefined ? "card__header" : "card__header card__header--row"}>
          <h2 className="card__title" id={titleId}>
            {title}
          </h2>
          {caption === undefined ? null : <span className="card__caption">{caption}</span>}
          {aside === undefined ? null : aside}
        </div>
      )}
      {children}
    </section>
  );
}
